import { logger } from "../../core/Logger.js";

export class HUDManager {
    constructor() {
        this._getElements();
        this.isVisible = false;
        logger.info("CORE", "DEBUG", "HUDManager initialisé");
    }

    _getElements() {
        this.mainHUD = document.getElementById("nexus-hud");

        // AI Panel
        this.aiBar     = document.getElementById("nexus-bar-fill");
        this.aiStatus  = document.getElementById("nexus-status");
        this.aiPattern = document.getElementById("nexus-pattern");

        // Health
        this.healthBar  = document.getElementById("health-bar-fill");
        this.healthText = document.getElementById("health-text");

        // Dash (skill slot + energy bar)
        this.dashIndicator = document.getElementById("dash-indicator");
        this.dashBarFill   = document.getElementById("dash-bar-fill");
        this.dashText      = document.getElementById("dash-text");

        // Power (skill slot + timer)
        this.powerIndicator = document.getElementById("power-indicator");
        this.powerName      = document.getElementById("power-name");
        this.powerTimerBar  = document.getElementById("power-timer-bar");

        // Slide + Jump skill slots
        this.slideIndicator = document.getElementById("slide-indicator");
        this.jumpIndicator  = document.getElementById("jump-indicator");

        // Floor
        this.floorName = document.getElementById("hud-floor-name");

        // Debug
        this.debugMonitor = document.getElementById("debug-monitor");
        this.mDash      = document.getElementById("m-dash");
        this.mDashAvg   = document.getElementById("m-dash-avg");
        this.mKills     = document.getElementById("m-kills");
        this.mStreak    = document.getElementById("m-streak");
        this.mDamage    = document.getElementById("m-damage");
        this.mCounter   = document.getElementById("m-counter");
        this.mAggr      = document.getElementById("m-aggr");
    }

    // ─── Affichage ──────────────────────────────────────────────────

    show() {
        if (this.mainHUD) {
            this.mainHUD.style.display = "block";
            this.isVisible = true;
        }
    }

    hide() {
        if (this.mainHUD) {
            this.mainHUD.style.display = "none";
            if (this.debugMonitor) this.debugMonitor.style.display = "none";
            this.isVisible = false;
        }
    }

    showDebug() {
        if (this.debugMonitor) this.debugMonitor.style.display = "block";
    }

    setFloor(floorName) {
        if (this.floorName) this.floorName.textContent = floorName ?? "—";
    }

    // ─── Update principal ────────────────────────────────────────────

    update(data) {
        if (!this.isVisible) return;
        this._updateAIPattern(data.aiProgress);
        this._updateHealth(data.health);
        this._updateDash(data.dash);
        this._updatePower(data.power);
        this._updateSlide(data.dash);
        this._updateJumpSlot(data.dash);
    }

    updateDebug(collector) {
        if (!this.isVisible) return;
        const d = collector.data;
        const totalMoves = d.movements.left + d.movements.right + d.movements.up + d.movements.down;

        document.getElementById("m-moves").innerText = totalMoves;
        this.mDash.innerText    = d.dash.count;
        this.mDashAvg.innerText = Math.round(d.dash.averageInterval);
        this.mKills.innerText   = d.combat.enemiesKilled;
        this.mStreak.innerText  = d.combat.killStreak;
        document.getElementById("m-dash-kills").innerText = d.combat.dashKills;
        this.mDamage.innerText  = d.combat.damageTaken;
        this.mCounter.innerText = Math.floor(collector.actionCounter);
        this.mAggr.innerText    = collector.getAggressionLevel().toFixed(2);
    }

    // ─── AI Pattern ─────────────────────────────────────────────────

    _updateAIPattern(progress) {
        if (!this.aiBar || !this.aiStatus || !this.aiPattern) return;

        this.aiBar.style.width = `${Math.min(progress, 100)}%`;

        if (progress > 80) {
            this.aiStatus.innerText = "CRITIQUE";
            this.aiStatus.style.color = "#ff3333";
            this.aiBar.style.background = "#ff3333";
            this.aiBar.style.boxShadow  = "none";
            this.aiPattern.innerText = "Adaptation imminente";
        } else if (progress > 40) {
            this.aiStatus.innerText = "INSTABLE";
            this.aiStatus.style.color = "#cc00ff";
            this.aiBar.style.background = "#cc00ff";
            this.aiBar.style.boxShadow  = "none";
            this.aiPattern.innerText = "Mouvements analysés";
        } else {
            this.aiStatus.innerText = "STABLE";
            this.aiStatus.style.color = "#ffffff";
            this.aiBar.style.background = "#ffffff";
            this.aiBar.style.boxShadow  = "none";
            this.aiPattern.innerText = "Recherche...";
        }
    }

    // ─── Santé ──────────────────────────────────────────────────────

    _updateHealth(health) {
        if (!this.healthBar || !this.healthText) return;

        this.healthBar.style.width = `${health.percentage}%`;
        this.healthText.innerText  = `${health.current}/${health.max}`;

        if (health.current <= 3) {
            this.healthBar.style.background  = "#ff3333";
            this.healthBar.style.boxShadow   = "none";
        } else if (health.current <= 6) {
            this.healthBar.style.background  = "#ffaa00";
            this.healthBar.style.boxShadow   = "none";
        } else {
            this.healthBar.style.background  = "#0092ff";
            this.healthBar.style.boxShadow   = "none";
        }
    }

    // ─── Dash ────────────────────────────────────────────────────────

    _updateDash(dash) {
        if (!this.dashIndicator) return;

        if (dash.isDashing) {
            this.dashIndicator.className = "skill-slot active";
            if (this.dashBarFill) {
                this.dashBarFill.style.width  = "100%";
                this.dashBarFill.style.background = "#ffffff";
                this.dashBarFill.style.boxShadow  = "none";
            }
            if (this.dashText) this.dashText.innerText = "DASH!";
        } else if (dash.isReady) {
            this.dashIndicator.className = "skill-slot ready";
            if (this.dashBarFill) {
                this.dashBarFill.style.width  = "100%";
                this.dashBarFill.style.background = "#cc00ff";
                this.dashBarFill.style.boxShadow  = "none";
            }
            if (this.dashText) this.dashText.innerText = "READY";
        } else {
            this.dashIndicator.className = "skill-slot cooldown";
            if (this.dashBarFill) {
                this.dashBarFill.style.width  = `${(dash.cooldownRemaining ?? 0)}%`;
                this.dashBarFill.style.background = "rgba(204, 0, 255, 0.2)";
                this.dashBarFill.style.boxShadow  = "none";
            }
            if (this.dashText) this.dashText.innerText = "CD";
        }
    }

    // ─── Power ───────────────────────────────────────────────────────

    _updatePower(power) {
        if (!this.powerIndicator) return;

        if (power.active) {
            this.powerIndicator.style.display = "flex";
            this.powerIndicator.className = `skill-slot power-${power.active}`;
            if (this.powerName) this.powerName.innerText = power.active.toUpperCase();
            // Barre de timer qui se vide
            if (this.powerTimerBar) {
                const pct = Math.max(0, power.timerPercent ?? 100);
                this.powerTimerBar.style.width = `${pct}%`;
                const colors = {
                    Force:   '#ff6600',
                    Vitesse: '#00ccff',
                };
                this.powerTimerBar.style.background = colors[power.active] || '#ffffff';
                this.powerTimerBar.style.display = 'block';
            }
        } else if (power.stored) {
            this.powerIndicator.style.display = "flex";
            this.powerIndicator.className = "skill-slot power-stored";
            if (this.powerName) this.powerName.innerText = power.stored.toUpperCase();
            if (this.powerTimerBar) this.powerTimerBar.style.display = 'none';
        } else {
            this.powerIndicator.style.display = "none";
        }
    }

    // ─── Slide slot ─────────────────────────────────────────────────

    _updateSlide(dash) {
        if (!this.slideIndicator) return;
        if (dash.isSliding) {
            this.slideIndicator.className = "skill-slot active";
        } else if (dash.isSlideReady) {
            this.slideIndicator.className = "skill-slot";
        } else {
            this.slideIndicator.className = "skill-slot cooldown";
        }
    }

    // ─── Jump slot ──────────────────────────────────────────────────

    _updateJumpSlot(dash) {
        if (!this.jumpIndicator) return;
        if (dash.isJumping) {
            this.jumpIndicator.className = "skill-slot jumping";
        } else if (dash.isJumpReady) {
            this.jumpIndicator.className = "skill-slot ready";
        } else {
            this.jumpIndicator.className = "skill-slot cooldown";
        }
    }

    // ─── Effets visuels ──────────────────────────────────────────────

    applyGlitchEffect() {
        const canvas = document.getElementById("renderCanvas");
        if (!canvas) return;
        canvas.style.filter = "brightness(2) hue-rotate(20deg)";
        setTimeout(() => { canvas.style.filter = ""; }, 120);
    }

    applyScanEffect() {}
}
